/**
 * L'instant de capture d'une trame qui part, retrouvé par son horodatage RTP.
 *
 * # Pourquoi
 *
 * Le worker voit une trame à la sortie de l'encodeur, pas à sa prise. La dater
 * à cet instant la mettrait en retard sur les notes de toute la durée de
 * l'encodage. Et cette durée varie — une image clé, une machine chargée — :
 * chaque spectateur rejouerait ces à-coups trame par trame. Le navigateur
 * pourrait donner l'instant de capture (`captureTime`) : Chromium 148 le donne
 * pour le son, pas pour l'image.
 *
 * # Comment
 *
 * L'horodatage RTP d'une trame est tiré de son instant de capture — à 90 kHz
 * pour une image, à 48 kHz pour le son d'Opus —, à un décalage près tiré au
 * hasard pour chaque flux. Deux trames d'un même flux sont donc séparées
 * exactement de ce qui sépare leurs prises. Pour poser ce fil sur l'horloge, la
 * trame la plus rapide des dernières secondes sert d'ancre. Chaque trame est
 * ainsi datée à sa cadence de prise, en retard de l'encodage le plus court —
 * quelques millisecondes — et non plus du sien.
 *
 * Une ancre par flux (`synchronizationSource`) : chaque couche de simulcast a
 * son propre décalage.
 */

/**
 * Combien de temps une trame sert d'ancre, en ms : de quoi suivre une horloge
 * qui dérive, ou une source qui change sous le même flux.
 */
export const ANCHOR_WINDOW_MS = 5_000;

/** L'horloge RTP d'une image : 90 kHz. */
export const VIDEO_RTP_TICKS_PER_MS = 90;

/** L'horloge RTP d'Opus : 48 kHz, quel que soit l'échantillonnage du micro. */
export const AUDIO_RTP_TICKS_PER_MS = 48;

const RTP_WRAP = 2 ** 32;
const RTP_HALF_WRAP = 2 ** 31;

/** Au-delà, les flux qu'on ne voit plus sont oubliés : une reconnexion en crée de nouveaux. */
const STREAMS_KEPT = 8;

interface Lag {
  at: number;
  lagMs: number;
}

interface StreamClock {
  lastRtp: number;
  /** L'horodatage déroulé, sans retour à zéro. */
  extended: number;
  lastSeenAt: number;
  /** Les ancres candidates, en écart croissant : un minimum glissant. */
  lags: Lag[];
}

export class CaptureClock {
  private readonly streams = new Map<number, StreamClock>();

  constructor(
    private readonly windowMs = ANCHOR_WINDOW_MS,
    private readonly ticksPerMs = VIDEO_RTP_TICKS_PER_MS,
  ) {}

  /**
   * L'instant de capture estimé d'une trame, sur l'horloge de `nowMs` ; `null`
   * sans horodatage RTP. Jamais après `nowMs`.
   */
  estimate(nowMs: number, ssrc: number | undefined, rtpTimestamp: number | undefined): number | null {
    if (rtpTimestamp === undefined || !Number.isFinite(rtpTimestamp)) {
      return null;
    }

    const stream = this.stream(ssrc ?? 0, rtpTimestamp, nowMs);
    const mediaMs = stream.extended / this.ticksPerMs;
    const lagMs = nowMs - mediaMs;
    const { lags } = stream;

    while (lags.length > 0 && lags[lags.length - 1].lagMs >= lagMs) {
      lags.pop();
    }

    lags.push({ at: nowMs, lagMs });

    while (lags[0].at < nowMs - this.windowMs) {
      lags.shift();
    }

    return mediaMs + lags[0].lagMs;
  }

  private stream(ssrc: number, rtpTimestamp: number, nowMs: number): StreamClock {
    const known = this.streams.get(ssrc);

    if (known !== undefined) {
      let step = rtpTimestamp - known.lastRtp;

      if (step >= RTP_HALF_WRAP) {
        step -= RTP_WRAP;
      } else if (step < -RTP_HALF_WRAP) {
        step += RTP_WRAP;
      }

      known.extended += step;
      known.lastRtp = rtpTimestamp;
      known.lastSeenAt = nowMs;

      return known;
    }

    if (this.streams.size >= STREAMS_KEPT) {
      for (const [key, stream] of this.streams) {
        if (stream.lastSeenAt < nowMs - this.windowMs) {
          this.streams.delete(key);
        }
      }
    }

    const created: StreamClock = { lastRtp: rtpTimestamp, extended: rtpTimestamp, lastSeenAt: nowMs, lags: [] };

    this.streams.set(ssrc, created);

    return created;
  }
}
